<template>
  <DefaultLayout>
    <div class="page-header">
      <h1>Planes y Precios</h1>
      <p class="subtitle">Elige el plan que mejor se adapte a tus necesidades</p>
    </div>

    <!-- Subscription Overview -->
    <Card v-if="subscriptionOverview" class="current-plan-card mb-4">
      <template #content>
        <div class="subscription-overview">
          <div class="current-plan-info">
            <i class="pi pi-check-circle" style="color: #10b981; font-size: 1.5rem;"></i>
            <div>
              <strong>Plan Actual:</strong> {{ subscriptionOverview.plan?.name || 'Sin plan' }}
              <Tag
                :value="subscriptionOverview.subscription_status"
                :severity="statusSeverity(subscriptionOverview.subscription_status)"
                class="ml-2"
              />
            </div>
          </div>
          <div class="subscription-metrics">
            <span><strong>Ejecuciones ({{ subscriptionOverview.usage_month }}):</strong> {{ subscriptionOverview.agent_executions_total }}</span>
            <span><strong>Tokens usados:</strong> {{ (subscriptionOverview.token_usage?.tokens_used || 0).toLocaleString() }}</span>
            <span><strong>Renovación:</strong> {{ formatDate(subscriptionOverview.current_subscription?.ends_at) }}</span>
          </div>
          <div v-if="enabledAgentsDisplay.length" class="enabled-agents">
            <strong>Agentes habilitados:</strong>
            <div class="agents-list">
              <Tag
                v-for="agent in enabledAgentsDisplay"
                :key="agent.agent_code"
                :value="`${agent.agent_name} (${agent.executions_count})`"
                severity="info"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>

    <div class="plans-grid" v-if="!loading">
      <Card v-for="plan in plans" :key="plan.id" class="plan-card" :class="{ 'current-plan': isCurrentPlan(plan) }">
        <template #title>
          <div class="plan-title">
            {{ plan.name }}
            <Tag v-if="isCurrentPlan(plan)" value="Plan Actual" severity="success" class="ml-2" />
          </div>
        </template>
        <template #subtitle>
          <span class="price">${{ (plan.monthly_price / 100).toLocaleString('es-CO') }} COP/mes</span>
        </template>
        <template #content>
          <ul class="features">
            <li><i class="pi pi-check" /> {{ plan.included_tokens ? plan.included_tokens.toLocaleString() + ' tokens/mes' : 'Tokens ilimitados' }}</li>
            <li><i class="pi pi-check" /> {{ plan.max_agents ?? 'Ilimitados' }} agentes</li>
            <li><i class="pi pi-check" /> Soporte {{ plan.support_level }}</li>
            <li v-if="plan.max_audio_minutes"><i class="pi pi-check" /> {{ plan.max_audio_minutes }} min audio/mes</li>
          </ul>
          <Button 
            :label="isCurrentPlan(plan) ? 'Plan Actual' : 'Suscribirse'" 
            class="w-full mt-3" 
            @click="subscribe(plan)"
            :disabled="subscribing || isCurrentPlan(plan)"
            :loading="subscribing && selectedPlanId === plan.id"
          />
        </template>
      </Card>
    </div>

    <div class="page-header mt-4">
      <h2>Paquetes de Tokens</h2>
      <p v-if="subscriptionOverview?.token_usage" class="token-balance">
        Balance actual: 
        <strong>{{ (subscriptionOverview.token_usage.tokens_used || 0).toLocaleString() }}</strong>
        /
        <strong>
          {{
            subscriptionOverview.token_usage.is_unlimited
              ? '∞'
              : (subscriptionOverview.token_usage.included_tokens || 0).toLocaleString()
          }}
        </strong>
        tokens usados
      </p>
    </div>
    <div class="plans-grid">
      <Card v-for="pkg in packages" :key="pkg.id" class="plan-card">
        <template #title>{{ pkg.name }}</template>
        <template #content>
          <p class="price">${{ (pkg.price_cop / 100).toLocaleString('es-CO') }} COP</p>
          <p>{{ pkg.token_count.toLocaleString() }} tokens</p>
          <Button 
            label="Comprar" 
            class="w-full mt-3" 
            severity="secondary" 
            @click="buyPackage(pkg)"
            :disabled="buying"
            :loading="buying && selectedPackageId === pkg.id"
          />
        </template>
      </Card>
    </div>

    <!-- Mis Pagos Section -->
    <div class="page-header mt-5">
      <h2>Mis Pagos</h2>
      <p class="subtitle">Historial de pagos y transacciones</p>
    </div>
    <Card class="payment-history-card">
      <template #content>
        <TenantPaymentHistory 
          :orders="paymentOrders" 
          :isAdmin="false"
          @link-regenerated="fetchPaymentOrders"
        />
      </template>
    </Card>

    <!-- Confirmation Dialog -->
    <Dialog v-model:visible="showConfirmDialog" header="Confirmar Cambio de Plan" :modal="true" :closable="true">
      <p>Ya tienes un plan activo. Si continúas, tu plan actual será reemplazado al completar el pago del nuevo plan.</p>
      <p><strong>Plan actual:</strong> {{ currentTenant?.plan_name }}</p>
      <p><strong>Nuevo plan:</strong> {{ selectedPlan?.name }}</p>
      <template #footer>
        <Button label="Cancelar" icon="pi pi-times" @click="showConfirmDialog = false" text />
        <Button label="Continuar" icon="pi pi-check" @click="confirmSubscribe" :loading="subscribing" />
      </template>
    </Dialog>
  </DefaultLayout>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '../stores/auth';
import DefaultLayout from '../components/layout/DefaultLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import TenantPaymentHistory from '../components/payments/TenantPaymentHistory.vue';
import api from '../services/api';

const toast = useToast();
const authStore = useAuthStore();

const plans = ref([]);
const packages = ref([]);
const loading = ref(false);
const subscribing = ref(false);
const buying = ref(false);
const selectedPlanId = ref(null);
const selectedPackageId = ref(null);
const selectedPlan = ref(null);
const showConfirmDialog = ref(false);
const currentTenant = ref(null);
const paymentOrders = ref([]);
const subscriptionOverview = ref(null);

const AGENT_ALIAS_TO_CODE = {
  'Agente Financiero': 'FINANCIAL_ANALYSIS_AGENT',
};

const enabledAgentsDisplay = computed(() => {
  const agents = subscriptionOverview.value?.enabled_agents ?? [];
  const grouped = new Map();

  for (const agent of agents) {
    const canonicalCode =
      AGENT_ALIAS_TO_CODE[agent.agent_name] ||
      agent.agent_code ||
      agent.agent_name;
    const canonicalName =
      canonicalCode === 'FINANCIAL_ANALYSIS_AGENT'
        ? 'FINANCIAL_ANALYSIS_AGENT'
        : agent.agent_name;

    const current = grouped.get(canonicalCode);
    if (current) {
      current.executions_count += Number(agent.executions_count || 0);
      continue;
    }

    grouped.set(canonicalCode, {
      agent_code: canonicalCode,
      agent_name: canonicalName,
      executions_count: Number(agent.executions_count || 0),
    });
  }

  return Array.from(grouped.values()).sort((a, b) =>
    a.agent_name.localeCompare(b.agent_name),
  );
});

// Fetch current tenant information
const fetchCurrentTenant = async () => {
  try {
    if (!authStore.user?.tenant_id) return;
    const response = await api.get(`/tenants/${authStore.user.tenant_id}`);
    currentTenant.value = response.data;
  } catch (error) {
    console.error('Error fetching tenant:', error);
  }
};

const fetchSubscriptionOverview = async () => {
  try {
    const response = await api.get('/payments/subscription/me');
    subscriptionOverview.value = response.data;
  } catch (error) {
    console.error('Error fetching subscription overview:', error);
  }
};

// Fetch payment orders for the current tenant
const fetchPaymentOrders = async () => {
  try {
    const response = await api.get('/payments/my-orders');
    paymentOrders.value = response.data;
  } catch (error) {
    console.error('Error fetching payment orders:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error al cargar el historial de pagos',
      life: 5000
    });
  }
};

const isCurrentPlan = (plan) => {
  const planId = subscriptionOverview.value?.plan_id ?? currentTenant.value?.plan_id;
  const status = subscriptionOverview.value?.subscription_status ?? currentTenant.value?.subscription_status;
  return planId === plan.id && status === 'active';
};

const statusSeverity = (status) => ({
  active: 'success',
  trial: 'info',
  past_due: 'warning',
  suspended: 'danger',
  cancelled: 'secondary',
  expired: 'secondary',
}[status] || 'secondary');

const formatDate = (value) => {
  if (!value) return 'N/D';
  return new Date(value).toLocaleDateString('es-CO');
};

const subscribe = async (plan) => {
  // Check if already on this plan
  if (isCurrentPlan(plan)) {
    return;
  }

  // Check if tenant has an active subscription
  if ((subscriptionOverview.value?.subscription_status || currentTenant.value?.subscription_status) === 'active') {
    selectedPlan.value = plan;
    showConfirmDialog.value = true;
    return;
  }

  // Proceed with subscription
  await proceedWithSubscription(plan);
};

const confirmSubscribe = async () => {
  showConfirmDialog.value = false;
  if (selectedPlan.value) {
    await proceedWithSubscription(selectedPlan.value);
  }
};

const proceedWithSubscription = async (plan) => {
  subscribing.value = true;
  selectedPlanId.value = plan.id;
  
  try {
    const response = await api.post(`/payments/subscribe/${plan.id}`);
    
    // Redirect to Epayco checkout
    if (response.data.payment_link_url) {
      window.location.href = response.data.payment_link_url;
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo generar el enlace de pago',
        life: 5000
      });
    }
  } catch (error) {
    console.error('Error subscribing:', error);
    const errorMessage = error.response?.data?.detail || 'Error al procesar la suscripción';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    subscribing.value = false;
    selectedPlanId.value = null;
  }
};

const buyPackage = async (pkg) => {
  buying.value = true;
  selectedPackageId.value = pkg.id;
  
  try {
    const response = await api.post(`/payments/buy-tokens/${pkg.id}`);
    
    // Redirect to Epayco checkout
    if (response.data.payment_link_url) {
      window.location.href = response.data.payment_link_url;
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo generar el enlace de pago',
        life: 5000
      });
    }
  } catch (error) {
    console.error('Error buying package:', error);
    const errorMessage = error.response?.data?.detail || 'Error al procesar la compra';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    buying.value = false;
    selectedPackageId.value = null;
  }
};

onMounted(async () => {
  loading.value = true;
  try {
    const [p, pk] = await Promise.all([
      api.get('/payments/plans').then(r => r.data),
      api.get('/payments/token-packages').then(r => r.data),
      fetchCurrentTenant(),
      fetchPaymentOrders(),
      fetchSubscriptionOverview(),
    ]);
    plans.value = p;
    packages.value = pk;
  } catch (e) { 
    console.error(e);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error al cargar planes y paquetes',
      life: 5000
    });
  }
  finally { loading.value = false; }
});
</script>

<style scoped>
.page-header { margin-bottom: 1.5rem; }
.page-header h1, .page-header h2 { margin-bottom: 0.25rem; color: #1e293b; }
.subtitle { color: #6b7280; }
.token-balance { color: #6b7280; margin-top: 0.5rem; }
.plans-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1.5rem; }
.plan-card { text-align: center; transition: transform 0.2s, box-shadow 0.2s; }
.plan-card:hover { transform: translateY(-4px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
.plan-card.current-plan { border: 2px solid #10b981; }
.plan-title { display: flex; align-items: center; justify-content: center; gap: 0.5rem; }
.price { font-size: 1.25rem; font-weight: 700; color: #6366f1; }
.features { list-style: none; padding: 0; text-align: left; }
.features li { padding: 0.3rem 0; color: #374151; }
.features .pi-check { color: #10b981; margin-right: 0.5rem; }
.w-full { width: 100%; }
.mt-3 { margin-top: 0.75rem; }
.mt-4 { margin-top: 2rem; }
.mt-5 { margin-top: 3rem; }
.mb-4 { margin-bottom: 1.5rem; }
.ml-2 { margin-left: 0.5rem; }
.current-plan-card { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); }
.current-plan-info { display: flex; align-items: center; gap: 1rem; }
.subscription-overview { display: flex; flex-direction: column; gap: 0.75rem; }
.subscription-metrics { display: flex; flex-wrap: wrap; gap: 1rem; color: #374151; }
.enabled-agents { display: flex; flex-direction: column; gap: 0.5rem; }
.agents-list { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.payment-history-card { margin-bottom: 2rem; }
</style>
